import { AnimatePresence } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'

import { emitter, NotificationAddEvent, NotificationRemoveEvent } from './events'
import { NotificationItem } from './NotificationItem'
import { NotificationType } from './type'

const MAX_NOTIFICATIONS = 5
const DEFAULT_DURATION = 5000

interface TimerInfo {
  timeoutId: ReturnType<typeof setTimeout>
  remainingTime: number
  startTime: number
}

export function NotificationContainer() {
  const [notifications, setNotifications] = useState<NotificationType[]>([])
  const [isHovered, setIsHovered] = useState(false)
  const timersRef = useRef<Map<string, TimerInfo>>(new Map())

  const removeNotification = useCallback((id: string) => {
    // Clear timer if exists
    const timerInfo = timersRef.current.get(id)
    if (timerInfo) {
      clearTimeout(timerInfo.timeoutId)
      timersRef.current.delete(id)
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const startTimer = useCallback(
    (id: string, duration: number) => {
      const timeoutId = setTimeout(() => {
        removeNotification(id)
      }, duration)

      timersRef.current.set(id, {
        timeoutId,
        remainingTime: duration,
        startTime: Date.now(),
      })
    },
    [removeNotification],
  )

  const pauseAllTimers = useCallback(() => {
    timersRef.current.forEach((timerInfo, id) => {
      clearTimeout(timerInfo.timeoutId)
      const elapsed = Date.now() - timerInfo.startTime
      timerInfo.remainingTime = Math.max(0, timerInfo.remainingTime - elapsed)
      timersRef.current.set(id, timerInfo)
    })
  }, [])

  const resumeAllTimers = useCallback(() => {
    timersRef.current.forEach((timerInfo, id) => {
      if (timerInfo.remainingTime > 0) {
        const timeoutId = setTimeout(() => {
          removeNotification(id)
        }, timerInfo.remainingTime)

        timersRef.current.set(id, {
          ...timerInfo,
          timeoutId,
          startTime: Date.now(),
        })
      }
    })
  }, [removeNotification])

  const addNotification = useCallback(
    (notification: NotificationType) => {
      setNotifications((prev) => {
        const newNotifications = [...prev, notification].slice(-MAX_NOTIFICATIONS)
        return newNotifications
      })

      // Start auto-dismiss timer if duration is not 0
      const duration = notification.duration ?? DEFAULT_DURATION
      if (duration > 0 && !isHovered) {
        startTimer(notification.id, duration)
      } else if (duration > 0) {
        // If hovered, store the timer info with full duration for later
        timersRef.current.set(notification.id, {
          timeoutId: setTimeout(() => {}, 0),
          remainingTime: duration,
          startTime: Date.now(),
        })
      }
    },
    [isHovered, startTimer],
  )

  useEffect(() => {
    emitter.on(NotificationAddEvent, addNotification)
    emitter.on(NotificationRemoveEvent, removeNotification)

    return () => {
      emitter.off(NotificationAddEvent, addNotification)
      emitter.off(NotificationRemoveEvent, removeNotification)
      // Clear all timers on unmount
      timersRef.current.forEach((timerInfo) => {
        clearTimeout(timerInfo.timeoutId)
      })
    }
  }, [addNotification, removeNotification])

  // Handle hover state changes
  useEffect(() => {
    if (isHovered) {
      pauseAllTimers()
    } else {
      resumeAllTimers()
    }
  }, [isHovered, pauseAllTimers, resumeAllTimers])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handleClose = useCallback(
    (id: string) => {
      removeNotification(id)
    },
    [removeNotification],
  )

  return (
    <div
      className="fixed top-6 right-6 z-[99] pointer-events-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`
          flex flex-col transition-all duration-300 ease-out
          ${isHovered ? 'gap-3' : 'gap-0'}
        `}
        style={{
          // When stacked, use absolute positioning for overlap effect
          position: 'relative',
        }}
      >
        <AnimatePresence mode="popLayout">
          {notifications.map((notification, index) => {
            const reversedIndex = notifications.length - 1 - index
            const showStacked = !isHovered && notifications.length > 1

            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={handleClose}
                className="pointer-events-auto"
                stacked={showStacked}
                stackIndex={showStacked ? reversedIndex : 0}
              />
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}
